import { CSSProperties, MouseEvent, useCallback, useRef, useState } from 'react';
import { autoUpdate, flip, offset, useFloating } from '@floating-ui/react-dom';

const HOVER_DELAY_MS = 1000;
const CORNER_CASE_TIMEOUT_MS = 500;

export type UsePlayerShieldPopoverProps = {
  isOpen?: boolean;
  setIsOpen: (isOpen: boolean) => void;
  style: CSSProperties;
  floatingRef: (node: HTMLElement | null) => void;
  onMouseLeave: () => void;
  onMouseEnter: () => void;
};
export function usePlayerShieldPopover(): {
  anchorRef: typeof floating.refs.setReference;
  open: (event: MouseEvent<HTMLDivElement>) => void;
  miniShieldMouseEnter: () => void;
  miniShieldMouseLeave: () => void;
  props: UsePlayerShieldPopoverProps;
} {
  const [status, setStatus] = useState<'open-by-hover' | 'open-by-click' | 'closed'>('closed');
  const hoverTimeoutRef = useRef<number | NodeJS.Timeout>(undefined);
  const cornerCaseLeaveTimeoutRef = useRef<number | NodeJS.Timeout>(undefined);

  const floating = useFloating({
    placement: 'top',
    whileElementsMounted: autoUpdate,
    // Поповер специально подвинут, чтобы перекрывать начальный элемент. Может не всегда работать, надо подумать
    middleware: [flip(), offset(0)],
  });

  const miniShieldMouseEnter = useCallback(() => {
    if (status !== 'closed') return;

    hoverTimeoutRef.current = setTimeout(() => {
      setStatus('open-by-hover');
    }, HOVER_DELAY_MS);
  }, [hoverTimeoutRef, setStatus, status]);

  const miniShieldMouseLeave = useCallback(() => {
    clearTimeout(hoverTimeoutRef.current);
    // Если мы открывали щит с клика, то не надо ничего делать, пока не кликнем.
    if (status !== 'open-by-hover') return;

    // Корнер кейс - если мы навели на мини-щит, показали большой, НЕ наводили на большой и увели мышку с мини-щита
    // В этом случае мы должны спрятать большой щит, но понять это мы можем только в таймауте (см. popoverMouseEnter).
    cornerCaseLeaveTimeoutRef.current = setTimeout(() => {
      setStatus('closed');
    }, CORNER_CASE_TIMEOUT_MS);
  }, [hoverTimeoutRef, status]);

  const popoverMouseLeave = useCallback(() => {
    // Если открыли с клика, то ничего не делаем
    if (status === 'open-by-click') return;

    setStatus('closed');
    setTimeout(() => {
      clearTimeout(hoverTimeoutRef.current);
    });
  }, [hoverTimeoutRef, setStatus, status]);

  const popoverMouseEnter = useCallback(() => {
    clearTimeout(cornerCaseLeaveTimeoutRef.current);
  }, []);

  return {
    anchorRef: floating.refs.setReference,
    open: useCallback(
      (event) => {
        event.stopPropagation();
        setStatus('open-by-click');
      },
      [setStatus],
    ),
    miniShieldMouseEnter,
    miniShieldMouseLeave,
    props: {
      isOpen: status === 'open-by-click' || status === 'open-by-hover',
      setIsOpen: (isOpen: boolean) => {
        clearTimeout(hoverTimeoutRef.current);
        isOpen ? setStatus('open-by-click') : setStatus('closed');
      },
      style: floating.floatingStyles,
      floatingRef: floating.refs.setFloating,
      onMouseLeave: popoverMouseLeave,
      onMouseEnter: popoverMouseEnter,
    },
  };
}
